
import { Sheet } from '@/model/Sheet';
import { Component, Prop, Vue } from 'vue-property-decorator';

export type SheetAction = 'update' | 'view' | 'edit' | 'share' | 'delete';

@Component
export default class SheetActionButton extends Vue {
    private static readonly LONG_PRESS_DURATION = 500;

    private static readonly TOUCH_MOVE_TOLERANCE = 10;

    @Prop({ required: true }) readonly sheet!: Sheet;

    @Prop({ required: true }) readonly action!: SheetAction;

    @Prop({ required: true, type: String }) readonly icon!: string;

    @Prop({ required: true, type: String }) readonly tooltipKey!: string;

    @Prop({ default: false, type: Boolean }) readonly isLink!: boolean;

    private isMobileTooltipVisible = false;

    private longPressTimer: number | null = null;

    private touchStartPoint: { x: number; y: number } | null = null;

    private longPressTriggered = false;

    private suppressNextClick = false;

    private suppressClickTimer: number | null = null;

    get isTooltipVisible(): boolean {
        return this.isMobileTooltipVisible;
    }

    get sheetRoute(): { name: string; params: { token: string } } {
        return {
            name: 'Sheet',
            params: {
                token: this.sheet.token,
            },
        };
    }

    startLongPress(event: TouchEvent): void {
        if (!this.isMobileViewport() || event.touches.length !== 1) {
            return;
        }

        const touch = event.touches[0];
        this.clearTouchState();
        this.touchStartPoint = { x: touch.clientX, y: touch.clientY };
        this.longPressTimer = globalThis.setTimeout(() => {
            this.longPressTimer = null;
            this.longPressTriggered = true;
            this.isMobileTooltipVisible = true;
        }, SheetActionButton.LONG_PRESS_DURATION);
    }

    onTouchEnd(event: TouchEvent): void {
        const wasLongPress = this.longPressTriggered;
        this.clearLongPressTimer();
        this.touchStartPoint = null;
        this.longPressTriggered = false;

        if (wasLongPress) {
            event.preventDefault();
            this.suppressNextClick = true;
            this.suppressClickTimer = globalThis.setTimeout(() => {
                this.suppressNextClick = false;
                this.suppressClickTimer = null;
            }, SheetActionButton.LONG_PRESS_DURATION);
        }
    }

    cancelTouch(): void {
        this.clearTouchState();
    }

    handleClick(event: MouseEvent): void {
        if (this.suppressNextClick) {
            event.preventDefault();
            event.stopPropagation();
            this.clearClickSuppression();
            return;
        }

        this.isMobileTooltipVisible = false;
        this.$emit('action-click', this.action, this.sheet);
    }

    beforeDestroy(): void {
        this.clearTouchState();
        this.clearClickSuppression();
    }

    private isMobileViewport(): boolean {
        return globalThis.innerWidth < 1024;
    }

    private onTouchMove(event: TouchEvent): void {
        if (!this.touchStartPoint || event.touches.length === 0) {
            return;
        }

        const touch = event.touches[0];
        const movedX = touch.clientX - this.touchStartPoint.x;
        const movedY = touch.clientY - this.touchStartPoint.y;
        const distance = Math.hypot(movedX, movedY);

        if (distance > SheetActionButton.TOUCH_MOVE_TOLERANCE) {
            this.clearTouchState();
        }
    }

    private clearLongPressTimer(): void {
        if (this.longPressTimer !== null) {
            globalThis.clearTimeout(this.longPressTimer);
            this.longPressTimer = null;
        }
    }

    private clearClickSuppression(): void {
        if (this.suppressClickTimer !== null) {
            globalThis.clearTimeout(this.suppressClickTimer);
            this.suppressClickTimer = null;
        }
        this.suppressNextClick = false;
    }

    private clearTouchState(): void {
        this.clearLongPressTimer();
        this.touchStartPoint = null;
        this.longPressTriggered = false;
        this.isMobileTooltipVisible = false;
    }
}
